// Format date utilities
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    return 'Invalid Date'
  }
}

export const formatDateShort = (dateString) => {
  if (!dateString) return 'N/A'
  
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  } catch (error) {
    return 'Invalid Date'
  }
}

export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'N/A'
  
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) {
      return 'Just now'
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `${hours} hour${hours > 1 ? 's' : ''} ago`
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400)
      return `${days} day${days > 1 ? 's' : ''} ago`
    } else {
      return formatDateShort(dateString)
    }
  } catch (error) {
    return 'Invalid Date'
  }
}

// File utilities
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export const getFileExtension = (filename) => {
  return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2)
}

export const isImageFile = (filename) => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
  const extension = getFileExtension(filename).toLowerCase()
  return imageExtensions.includes(extension)
}

// Validation utilities
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

export const validatePassword = (password) => {
  // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
  const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$/
  return re.test(password)
}

export const validatePhone = (phone) => {
  // Basic phone validation - can be enhanced
  const re = /^[\+]?[1-9][\d]{0,15}$/
  return re.test(phone.replace(/\s/g, ''))
}

// Status utilities
export const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'verified':
      return 'text-success-600 bg-success-100'
    case 'pending':
      return 'text-warning-600 bg-warning-100'
    case 'rejected':
      return 'text-danger-600 bg-danger-100'
    case 'suspicious':
      return 'text-red-600 bg-red-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

export const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'verified':
      return '✅'
    case 'pending':
      return '⏳'
    case 'rejected':
      return '❌'
    case 'suspicious':
      return '⚠️'
    default:
      return '❓'
  }
}

// Confidence score utilities
export const formatConfidence = (score) => {
  if (score === null || score === undefined) return 'N/A'
  return `${Math.round(score * 100)}%`
}

export const getConfidenceColor = (score) => {
  if (score === null || score === undefined) return 'text-gray-500'
  
  if (score >= 0.8) return 'text-success-600'
  if (score >= 0.6) return 'text-warning-600'
  return 'text-danger-600'
}

// Document type utilities
export const getDocumentTypeLabel = (type) => {
  const labels = {
    'id': 'ID Card',
    'passport': 'Passport',
    'license': 'Driver\'s License',
    'utility_bill': 'Utility Bill',
    'bank_statement': 'Bank Statement',
    'other': 'Other Document'
  }
  return labels[type] || type
}

export const getDocumentTypeIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'id':
      return '🆔'
    case 'passport':
      return '📘'
    case 'license':
      return '🚗'
    case 'utility_bill':
      return '📄'
    case 'bank_statement':
      return '🏦'
    default:
      return '📋'
  }
}

// Error handling utilities
export const getErrorMessage = (error) => {
  if (error.response?.data?.error) {
    return error.response.data.error
  }
  if (error.message) {
    return error.message
  }
  return 'An unexpected error occurred'
}

// Location utilities
export const formatLocation = (lat, lng) => {
  if (!lat || !lng) return 'Unknown Location'
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
}

export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371 // Radius of Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c // Distance in kilometers
}

// Number utilities
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0'
  return num.toLocaleString()
}

export const formatPercentage = (value, total) => {
  if (!total || total === 0) return '0%'
  return `${Math.round((value / total) * 100)}%`
}

// URL utilities
export const generateQRCodeURL = (data) => {
  // Simple QR code URL generation - can be enhanced with actual QR library
  return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data)}`
}

// Debounce utility
export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Local storage utilities
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error('Error saving to localStorage:', error)
  }
}

export const getLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error('Error reading from localStorage:', error)
    return defaultValue
  }
}

export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Error removing from localStorage:', error)
  }
}

// Copy to clipboard utility
export const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    console.error('Failed to copy to clipboard:', error)
    return false
  }
}